import React from 'react'
import {
  Box,
  Card,
  Divider,
  FinalCheckboxField as Checkbox,
  FinalNumberTypeField,
  FinalRadioField as Radio,
  FinalSelectField as Select,
  FinalTextArea as TextArea,
  FinalTextField as TextField,
  Flex,
  Option,
  ScreenReaderOnly,
  Text,
} from '@chilipiper/design-system'
import { FieldType } from '@chilipiper/meetings-components/src/guest-forms/types'

type Props = {
  autoFocus?: boolean
  customSelector?: string
  label: string
  name: string
  options?: Array<{ label: string; value: string }>
  required?: boolean
  type: FieldType
}

export const GuestFormsCFAField = ({
  autoFocus,
  name,
  label,
  type,
  options = [],
  required,
  customSelector,
}: Props) => {
  switch (type) {
    case FieldType.Email:
    case FieldType.Phone:
    case FieldType.SingleLine:
      return (
        <TextField
          autoComplete={type === FieldType.Phone ? 'tel' : 'on'}
          type={type === FieldType.Email ? 'email' : 'text'}
          data-test-id={`field-${name}`}
          data-chili-css={customSelector}
          h={11}
          name={name}
          label={label}
          id={name}
          autoFocus={autoFocus}
          required={required}
        />
      )
    case FieldType.Number:
      return (
        <FinalNumberTypeField
          data-test-id={`field-${name}`}
          data-chili-css={customSelector}
          name={name}
          label={label}
          id={name}
          required={required}
          autoFocus={autoFocus}
        />
      )
    case FieldType.Multiline:
      return (
        <TextArea
          data-chili-css={customSelector}
          h={15}
          name={name}
          label={label}
          required={required}
          autoFocus={autoFocus}
        />
      )
    case FieldType.MultipleSelection:
      return (
        <Box>
          <Box mb={2} px={4}>
            <Text
              data-test-id={`field-${name}`}
              data-chili-css={customSelector}
              fontSize={12}
              fontWeight={600}
              color='neutral-dark-48'
            >
              {label}
              {required && (
                <Text ml={1} textStyle='form-label' color='text/action-caution'>
                  *<ScreenReaderOnly>required</ScreenReaderOnly>
                </Text>
              )}
            </Text>
          </Box>
          <Card p={0}>
            {options.map((option, index) => (
              <React.Fragment key={`chk:${option.label}:${option.value}`}>
                <Flex h={9} alignItems='center'>
                  <Checkbox
                    data-test-id={`field-${name}-${option.label}`}
                    w='100%'
                    h={9}
                    px={4}
                    key={`chk:${option.label}:${option.value}`}
                    name={name}
                    label={option.label}
                    value={option.value}
                  />
                </Flex>
                {index !== options.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </Card>
        </Box>
      )
    case FieldType.SingleSelection:
      return (
        <Box data-test-id={`${type}-${label}`}>
          <Box mb={2} px={4}>
            <Text
              data-chili-css={customSelector}
              fontSize={12}
              fontWeight={600}
              color='neutral-dark-48'
            >
              {label}
              {required && (
                <Text ml={1} textStyle='form-label' color='text/action-caution'>
                  *
                </Text>
              )}
            </Text>
          </Box>
          <Card p={0} boxShadow='box-neutral-default-out' overflow='hidden'>
            {options.map((option, index) => (
              <Box key={`rd:${name}:${option.label}`}>
                <Radio
                  required={required}
                  id={`${name}:${option.label}`}
                  name={name}
                  data-test-id={`field-${name}-${option.label}`}
                  value={option.value}
                  label={option.label}
                  separator={index !== options.length - 1}
                />
              </Box>
            ))}
          </Card>
        </Box>
      )
    case FieldType.Select:
      return (
        <Select
          data-test-id={`field-${name}`}
          data-chili-css={customSelector}
          name={name}
          id='select'
          label={label}
          height={64}
          autoFocus={autoFocus}
          required={required}
        >
          {options.map(option => (
            <Option
              data-test-id={`field-${name}-${option.label}`}
              key={`sl:${option.label}:${option.value}`}
              value={option.value}
            >
              <Text>{option.label}</Text>
            </Option>
          ))}
        </Select>
      )
    default:
      return (
        <TextArea
          data-test-id={`field-${name}`}
          data-chili-css={customSelector}
          required={required}
          name={name}
          label={label}
          autoFocus={autoFocus}
        />
      )
  }
}
